import React, { useEffect, useState } from 'react';
import { useData } from '../DataContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Plus, Trash2, Edit, Calendar, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import useScrambleText from '../../hooks/useScrambleText';
import ImageUpload from '../ImageUpload';

const AdminEvents = () => {
  const { events, addEvent, updateEvent, deleteEvent, loading } = useData();
  const [isOpen, setIsOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { register, handleSubmit, reset, setValue } = useForm();
  const h1Ref = useScrambleText("Events Manager");
  const [eventImage, setEventImage] = useState('');

  useEffect(() => {
    if (editingEvent) {
      reset({
        title: editingEvent.title,
        date: editingEvent.date,
        time: editingEvent.time,
        venue: editingEvent.venue,
        description: editingEvent.description,
        ticketLink: editingEvent.ticketLink,
      });
      setEventImage(editingEvent.imageUrl || '');
    } else {
      reset({
        title: '',
        date: '',
        time: '',
        venue: '',
        description: '',
        ticketLink: '',
      });
      setEventImage('');
    }
  }, [editingEvent, reset]);

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const eventData = { ...data, imageUrl: eventImage };
      if (editingEvent) {
        await updateEvent(editingEvent.id, eventData);
      } else {
        await addEvent(eventData);
      }
      handleClose();
    } catch (error) {
      console.error('Failed to save event:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageChange = (newImageUrl) => {
    setEventImage(newImageUrl);
    setValue('imageUrl', newImageUrl);
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setIsOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await deleteEvent(id);
      } catch (error) {
        console.error('Failed to delete event:', error);
      }
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setEditingEvent(null);
    reset();
    setEventImage('');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 ref={h1Ref} className="text-3xl font-heading font-bold korov-gradient-text mb-2 cursor-pointer">Events Manager</h1>
          <p className="text-muted-foreground">Manage upcoming events and shows</p>
        </div>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary text-black hover:bg-primary/80" data-testid="add-event-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Event
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-card">
            <DialogHeader>
              <DialogTitle className="text-2xl font-heading text-primary">
                {editingEvent ? 'Edit Event' : 'Add New Event'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Event Title</label>
                <Input 
                  {...register('title', { required: true })} 
                  placeholder="e.g., Dharmar Night"
                  className="bg-background/50"
                  data-testid="event-title-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Date</label>
                  <Input 
                    type="date"
                    {...register('date', { required: true })} 
                    className="bg-background/50"
                    data-testid="event-date-input"
                  />
                </div>
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Time</label>
                  <Input 
                    type="time"
                    {...register('time', { required: true })} 
                    placeholder="23:00"
                    className="bg-background/50"
                    data-testid="event-time-input"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Venue</label>
                <Input 
                  {...register('venue', { required: true })} 
                  placeholder="e.g., Club Terminus, Belgrade"
                  className="bg-background/50"
                  data-testid="event-venue-input"
                />
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Description</label>
                <Textarea 
                  {...register('description', { required: true })} 
                  placeholder="Event description..."
                  className="bg-background/50 min-h-[100px]"
                  data-testid="event-description-input"
                />
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Event Image</label>
                <ImageUpload 
                  value={eventImage}
                  onChange={handleImageChange}
                  label="Upload Event Image"
                />
                <p className="text-xs text-muted-foreground mt-2">Or enter image URL:</p>
                <Input 
                  value={eventImage}
                  onChange={(e) => handleImageChange(e.target.value)}
                  placeholder="https://..."
                  className="bg-background/50 mt-2"
                  data-testid="event-image-input"
                />
              </div>

              <div>
                <label className="text-sm text-muted-foreground mb-2 block">Ticket Link (optional)</label>
                <Input 
                  {...register('ticketLink')} 
                  placeholder="https://..."
                  className="bg-background/50"
                  data-testid="event-ticket-input"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  type="submit" 
                  className="flex-1 bg-primary text-black hover:bg-primary/80"
                  disabled={isSubmitting}
                  data-testid="event-submit-btn"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingEvent ? 'Update Event' : 'Add Event'}
                </Button>
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-white/10 rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10">
              <TableHead className="text-primary">Event</TableHead>
              <TableHead className="text-primary">Date</TableHead>
              <TableHead className="text-primary">Venue</TableHead>
              <TableHead className="text-primary text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                  No events yet. Add your first event!
                </TableCell>
              </TableRow>
            ) : (
              events.map((event) => (
                <TableRow key={event.id} className="border-white/10" data-testid={`event-row-${event.id}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-primary" />
                      <div>
                        <div className="font-heading text-white">{event.title}</div>
                        <div className="text-xs text-muted-foreground">{event.time}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatDate(event.date)}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {event.venue}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(event)}
                        className="hover:bg-primary/10 hover:text-primary"
                        data-testid={`edit-event-${event.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(event.id)}
                        className="hover:bg-destructive/10 hover:text-destructive"
                        data-testid={`delete-event-${event.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default AdminEvents;
